import React from 'react';
import styled from 'styled-components';

// Styled Components
const SectionWrapper = styled.section`
  padding: 100px 0;
  background-color: var(--bg-white);
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled.div`
  background-color: var(--bg-white);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: var(--shadow-xl);
  }
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CustomerAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 1rem;
  flex-shrink: 0;
`;

const CustomerDetails = styled.div`
  flex: 1;
`;

const CustomerName = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: var(--text-dark);
`;

const CustomerRole = styled.p`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const TestimonialText = styled.p`
  color: var(--text-dark);
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const RatingStars = styled.div`
  display: flex;
  color: var(--warning);
`;

// SVG Components
const StarIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.99984 1.66669L12.5748 6.88335L18.3332 7.72502L14.1665 11.7834L15.1498 17.5167L9.99984 14.8084L4.84984 17.5167L5.83317 11.7834L1.6665 7.72502L7.42484 6.88335L9.99984 1.66669Z" />
    </svg>
);

const HalfStarIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.99984 1.66669L12.5748 6.88335L18.3332 7.72502L14.1665 11.7834L15.1498 17.5167L9.99984 14.8084L4.84984 17.5167L5.83317 11.7834L1.6665 7.72502L7.42484 6.88335L9.99984 1.66669Z" fillOpacity="0.5" />
        <path d="M10 1.66669V14.8084L4.85 17.5167L5.83333 11.7834L1.66667 7.72502L7.425 6.88335L10 1.66669Z" />
    </svg>
);

// Customer Avatar SVGs
const AvatarChen = () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#E9D5FF" />
        <circle cx="24" cy="19" r="9" fill="#4B5563" />
        <path d="M10 44C10 36.268 16.268 30 24 30C31.732 30 38 36.268 38 44V48H10V44Z" fill="#4B5563" />
    </svg>
);

const AvatarLin = () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#DBEAFE" />
        <circle cx="24" cy="19" r="9" fill="#4B5563" />
        <path d="M10 44C10 36.268 16.268 30 24 30C31.732 30 38 36.268 38 44V48H10V44Z" fill="#4B5563" />
    </svg>
);

const AvatarWang = () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#FEF3C7" />
        <circle cx="24" cy="19" r="9" fill="#4B5563" />
        <path d="M10 44C10 36.268 16.268 30 24 30C31.732 30 38 36.268 38 44V48H10V44Z" fill="#4B5563" />
    </svg>
);

// Rating Component
const Rating = ({ stars }) => {
    const fullStars = Math.floor(stars);
    const hasHalfStar = stars % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
        <RatingStars>
            {[...Array(fullStars)].map((_, i) => (
                <StarIcon key={`full-${i}`} />
            ))}
            {hasHalfStar && <HalfStarIcon />}
            {[...Array(emptyStars)].map((_, i) => (
                <StarIcon key={`empty-${i}`} style={{ opacity: 0.3 }} />
            ))}
        </RatingStars>
    );
};

// Testimonials Component
const Testimonials = () => {
    // Testimonial data
    const testimonials = [
        {
            id: 1,
            name: "陳志明",
            role: "蔬果大學 執行長",
            text: "自從導入 ShopWave 後，我們的線上訂單數量成長了 76%，客戶也對新的購物體驗讚譽有加。系統穩定且易於管理，是我們業務成長的重要推手。",
            rating: 5,
            avatar: <AvatarChen />
        },
        {
            id: 2,
            name: "林美琪",
            role: "LuxStyle 品牌總監",
            text: "ShopWave 幫助我們打造出符合品牌調性的精品購物應用，不僅視覺美觀，購物流程也非常順暢。最重要的是，數據分析工具讓我們更了解客戶需求。",
            rating: 4.5,
            avatar: <AvatarLin />
        },
        {
            id: 3,
            name: "王建國",
            role: "TechStore 營運總監",
            text: "我們的電子產品需要詳細的規格比較和客製化功能，ShopWave 團隊完全理解我們的需求，並提供了超出預期的解決方案。客戶體驗大幅提升！",
            rating: 5,
            avatar: <AvatarWang />
        }
    ];

    return (
        <SectionWrapper>
            <SectionContainer>
                <SectionHeader>
                    <SectionTitle>客戶怎麼說</SectionTitle>
                    <SectionDescription>
                        來自實際使用 ShopWave 的客戶真實評價
                    </SectionDescription>
                </SectionHeader>

                <TestimonialsGrid>
                    {testimonials.map(testimonial => (
                        <TestimonialCard key={testimonial.id}>
                            <CustomerInfo>
                                <CustomerAvatar>
                                    {testimonial.avatar}
                                </CustomerAvatar>
                                <CustomerDetails>
                                    <CustomerName>{testimonial.name}</CustomerName>
                                    <CustomerRole>{testimonial.role}</CustomerRole>
                                </CustomerDetails>
                            </CustomerInfo>
                            <TestimonialText>"{testimonial.text}"</TestimonialText>
                            <Rating stars={testimonial.rating} />
                        </TestimonialCard>
                    ))}
                </TestimonialsGrid>
            </SectionContainer>
        </SectionWrapper>
    );
};

export default Testimonials;